import { useState, useEffect, useCallback } from "react";
import { MediaDevices, MediaDevicesEvents, PermissionStatus } from "../../utils/media-devices-utils";

export function useMicrophoneStatus(scene) {
  const mediaDevicesManager = APP.mediaDevicesManager;
  const mediaDevicesManagerPermission = APP.mediaDevicesManagerPermissions;
  const [isMicMuted, setIsMicMuted] = useState(!mediaDevicesManager.isMicEnabled);
  const [isMicEnabled, setIsMicEnabled] = useState(APP.mediaDevicesManager.isMicShared);
  const [isMicStarting, setIsMicStarting] = useState(false);
  const [permissionStatus, setPermissionsStatus] = useState(
    mediaDevicesManager.getPermissionsStatus(MediaDevices.MICROPHONE)
  );

  const setupMicListener = useCallback(() => {
    mediaDevicesManagerPermission.setupMicListener();
  }, [mediaDevicesManagerPermission]);

  useEffect(() => {
    const onMicMutedStateChanged = ({ enabled }) => {
      setIsMicMuted(!enabled);
    };
    APP.dialog.on("mic-state-changed", onMicMutedStateChanged);

    const onMicEnabled = () => {
      setIsMicEnabled(true);
      setIsMicStarting(false);
    };
    const onMicDisabled = () => {
      setIsMicEnabled(false);
      setIsMicStarting(false);
    };
    scene.addEventListener(MediaDevicesEvents.MIC_SHARE_ENDED, onMicDisabled);
    scene.addEventListener(MediaDevicesEvents.MIC_SHARE_STARTED, onMicEnabled);

    const onPermissionsChanged = ({ mediaDevice, status }) => {
      if (mediaDevice === MediaDevices.MICROPHONE) {
        setPermissionsStatus(status);
        if (status === PermissionStatus.DENIED) {
          setIsMicStarting(false);
        }
      }
    };
    mediaDevicesManager.on(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, onPermissionsChanged);

    return () => {
      APP.dialog.off("mic-state-changed", onMicMutedStateChanged);
      scene.removeEventListener(MediaDevicesEvents.MIC_SHARE_ENDED, onMicDisabled);
      scene.removeEventListener(MediaDevicesEvents.MIC_SHARE_STARTED, onMicEnabled);
      mediaDevicesManager.off(MediaDevicesEvents.PERMISSIONS_STATUS_CHANGED, onPermissionsChanged);
    };
  }, [setIsMicMuted, setIsMicEnabled, setIsMicStarting, setPermissionsStatus, scene, mediaDevicesManager]);

  const toggleMute = useCallback(async () => {
    if (isMicStarting) {
      return;
    }

    if (mediaDevicesManager.isMicShared) {
      mediaDevicesManager.toggleMic();
    } else {
      setIsMicStarting(true);
      try {
        await mediaDevicesManager.startMicShare({ unmute: true });
      } catch (error) {
        console.error("Error starting microphone share: ", error);
      } finally {
        setIsMicStarting(false);
      }
    }
  }, [isMicStarting, mediaDevicesManager]);

  const prepareMic = useCallback(() => {
    if (permissionStatus !== PermissionStatus.GRANTED || mediaDevicesManager.isMicShared) {
      return;
    }

    mediaDevicesManager.prepareMutedMicShare().catch(error => {
      console.warn("Unable to prepare muted microphone capture.", error);
    });
  }, [mediaDevicesManager, permissionStatus]);

  return {
    isMicMuted,
    toggleMute,
    prepareMic,
    isMicEnabled,
    isMicStarting,
    permissionStatus,
    setupMicListener
  };
}
